import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  DestroyRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { finalize, filter, map, distinctUntilChanged, BehaviorSubject, Subscription, switchMap } from 'rxjs';

import {
  TSIconModule,
  TsBadgeModule,
  TsFilterModule,
  TsButtonModule,
  TsOverlayModule,
  TsSnackbarService,
  TsOverlayComponent,
  TsColorBubbleComponent,
  FilterListItemModel,
  FilterListItemValueModel,
  FilterListItemChangeArgs,
} from 'tsui';
import { RelatedToType } from '@shared/enums';
import { OriginalStates } from 'app/shared/models';
import { FieldDefsState } from '@state/fields/state';
import { FieldDef, QueryConfig } from 'app/core/models';
import { TaskActions } from '@tasks/state/actions';
import { Task, TaskList } from 'app/shared/modules/legacy-tasks/tasks.types';
import { LegacyTasksTabService } from '@shared/modules/legacy-tasks/services/tasks.service';
import { FormFieldsModule } from '@shared/components/form-fields/form-fields.module';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { CellValueChangedEvent } from 'ag-grid-enterprise';
import { FeatureFlag } from '@core/enums';
import { AccountService } from '@core/services';
import { AccountColorBackgroundDirective } from '@shared/directives/account-color-background.directive';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsTableComponent } from 'tsui/lib/ts-table';
import { Actions, ofActionCompleted } from '@ngxs/store';
import { TaskUtils } from '@shared/modules/legacy-tasks/tasks.utils';

const MATERIAL_MODULES = [
  MatFormFieldModule,
  MatSelectModule,
  MatMenuModule,
  MatCheckboxModule,
  MatTableModule,
  MatSortModule,
  MatIconModule,
  MatTooltipModule,
  MatButtonModule,
  MatProgressSpinnerModule,
  MatProgressBarModule,
  MatDividerModule,
];

const TSUI_MODULES = [TsButtonModule, TSIconModule, TsOverlayModule, TsBadgeModule, TsColorBubbleComponent];

@Component({
  selector: 'project-panel-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormFieldsModule,
    TsFilterModule,
    TsTableComponent,
    AccountColorBackgroundDirective,
    ...MATERIAL_MODULES,
    ...TSUI_MODULES,
  ],
})
export class PanelsTasksComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  private tasks: Task[];
  private fieldDefs: FieldDef[] = [];
  private changeTriggers: Set<string> = new Set();
  private originalStates: OriginalStates;

  openTasks: Task[];
  displayColumns: string[] = ['is_complete', 'title', 'due_date', 'assigned_user'];
  tasksCount: {
    completed: number;
    incomplete: number;
    total: number;
  } = {
    completed: 0,
    incomplete: 0,
    total: 0,
  };
  projectTaskLists: TaskList[];
  selectedTaskList: TaskList;
  isLoading: boolean = false;
  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  queryFilters: Partial<QueryConfig.QueryFilters>;

  @ViewChild('filtersOverlayBox') private _filtersOverlayBox: TsOverlayComponent;

  get filterCounter(): number {
    return this.filterValueList?.value?.length || 0;
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(TsSnackbarService);

  columnDefs: ColumnDef[] = [];
  newTable: boolean;

  constructor(
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly tasksService: LegacyTasksTabService,
    private readonly accountService: AccountService,
    private readonly actions: Actions,
  ) {
    super(cdRef);
  }

  ngOnInit(): void {
    this.checkTable();
    this.setupTasksPanel();
    this.actions
      .pipe(
        ofActionCompleted(TaskActions.SetRelatedTo),
        switchMap(() => this.tasksService.getTaskLists()),
        filter((res) => !!res),
        map(({ task_lists }) => {
          this.projectTaskLists = task_lists;
          this.selectedTaskList = this.selectTaskListByPriority();
          // Set filters on load if any exists.
          this.queryFilters = this.getQueryFiltersState();
          this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
          if (this.selectedTaskList) {
            this.getTasks(this.selectedTaskList.id);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.cdRef.detectChanges());

    this.store.dispatch(new TaskActions.SetRelatedTo(RelatedToType.Project, this.project.id));
    this.setColDefs();
  }

  ngAfterViewInit(): void {
    this.closeSnackBarOnLeave();
  }

  private setupTasksPanel(): void {
    this.titleFieldDef = {
      name: 'tasks.panel_name',
      label: this.panelDef.title,
      type: 'text',
    };
    this.setFilteredFields();
  }

  private selectTaskListByPriority(): TaskList | undefined {
    const priorities = [
      { id: this.panelDef.meta?.last_selected_task_list_id },
      { id: this.project.default_task_list_id },
    ];

    if (this.projectTaskLists.length) priorities.push({ id: this.projectTaskLists[0].id });

    for (const { id } of priorities) {
      const selected = this.projectTaskLists.find((taskList) => taskList.id === id);
      if (selected) {
        return selected;
      }
    }
    return undefined;
  }

  private getTasks(taskListId?: number): Subscription {
    if (this.hasFiltersApplied()) {
      this.applyFilter(this.queryFilters);
    } else {
      if (!taskListId) taskListId = this.project?.default_task_list_id;
      this.isLoading = true;
      return this.tasksService
        .getTasks(taskListId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((tasks: Task[]) => {
          if (tasks) {
            this.tasks = TaskUtils.flattenTasks(TaskUtils.deepSortTasksByRank(tasks));
            this.loadTasks(this.tasks);
            this.isLoading = false;
            this.cdRef.markForCheck();
          }
        });
    }
  }

  private loadTasks(tasks: Task[]): void {
    this.tasksCount.total = tasks?.filter((task) => task.task_type === 'task').length;
    this.tasksCount.completed = tasks?.filter((task) => task.task_type === 'task' && task.is_complete).length;
    this.tasksCount.incomplete = this.tasksCount.total - this.tasksCount.completed;
    this.openTasks = TaskUtils.flattenTasks(TaskUtils.deepSortTasksByRank(tasks));
    this.openTasks = this.openTasks.filter(
      (task, index, self) =>
        (task.task_type === 'task' || task.task_type === 'key_date') &&
        index === self.findIndex((t) => t.id === task.id),
    );
  }

  private closeSnackBarOnLeave(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((route) => route instanceof NavigationStart),
      )
      .subscribe(() => this.snackBar.dismiss());
  }

  private hasFiltersApplied(): boolean {
    return this.queryFilters?.fjson?.length && this.queryFilters?.fjson !== '[]';
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  private formatFieldOptions(meta: any): string[] | { label: string; value: string }[] {
    const dropdownOptions = this.store.selectSnapshot(FieldDefsState.selectDropdownOptionsBy(meta?.option_name));

    if (dropdownOptions) {
      return dropdownOptions.map((option) => ({ label: option.name, value: option.id }));
    }

    return meta?.options || [];
  }

  private setFilteredFields(): void {
    this.fieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Task, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );
    this.setFilterNestedFields();
  }

  private setFilterNestedFields(): void {
    const allfields = this.fieldDefs.map((x) => ({
      ...x,
      meta: { ...x.meta, group: { key: 'Task', label: 'Task' } },
    }));

    this.typeFields.next(
      allfields.map(({ label, name, type, meta }) => {
        const fieldOptions = this.formatFieldOptions(meta);

        return {
          label,
          name,
          meta: {
            filtering: { operators: meta?.filtering?.operators || [], functions: meta?.filtering?.functions || [] },
            options: fieldOptions,
            multi_select: meta?.multi_select || false,
            record_type: meta?.record_type || null,
            group: meta?.group,
            scale: meta?.scale,
          },
          type,
        } as FilterListItemModel;
      }),
    );
  }

  private getQueryFiltersState(): Partial<QueryConfig.QueryFilters> {
    return JSON.parse(this.panelDef.meta?.filters || '{}');
  }

  private setQueryFiltersState(value: Partial<QueryConfig.QueryFilters>): void {
    this.originalStates.queryFilters = value;
  }

  private getFilterValueListState(): FilterListItemValueModel[] {
    return this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]');
  }

  private setFilterValueListState(value: FilterListItemValueModel[]): void {
    this.originalStates.filterValueList = value;
  }

  private captureOriginalStates(): void {
    this.originalStates = {
      queryFilters: { ...this.getQueryFiltersState() },
      filterValueList: this.getFilterValueListState(),
    };
  }

  private openTrackStateSnackbar(changeType: 'filters'): void {
    this.changeTriggers.add(changeType);
    this.captureOriginalStates();
    this.snackBar.openApplyChanges(this.resetToOriginalStates.bind(this), this.saveAllChanges.bind(this));
  }

  private clearChangeTriggers(): void {
    this.changeTriggers.clear();
  }

  private resetToOriginalStates(): void {
    if (this.changeTriggers.has('filters')) {
      this.queryFilters = this.getQueryFiltersState();
      this.filterValueList.next(this.getFilterValueListState());
      this.onCloseByBackdropClick();
      this.applyFilter(this.queryFilters);
    }

    this.clearChangeTriggers();
    this.changeDetectorRef.markForCheck();
  }

  private saveAllChanges(): void {
    if (this.changeTriggers.has('filters')) {
      this.panelDef = {
        ...this.panelDef,
        meta: {
          ...this.panelDef.meta,
          filters: JSON.stringify(this.queryFilters),
        },
      };

      this.onEditPanelDef.emit(this.panelDef);
      this.setQueryFiltersState(this.queryFilters);
      this.setFilterValueListState(this.filterValueList.value);
    }

    this.clearChangeTriggers();
  }

  private applyFilter(filters: Partial<QueryConfig.QueryFilters>): void {
    this.isLoading = true;

    const tempFilter = filters.fjson ? JSON.parse(filters.fjson) : [];

    tempFilter.push(['task_list_id', 'eq', this.selectedTaskList?.id]);

    this.tasksService
      .search({
        fjson: JSON.stringify(tempFilter),
        sort: [],
        q: null,
        include_keydates: false,
        select: `id,task_type,${this.displayColumns.join(',')}`,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((tasks: Task[]) => {
        this.tasks = TaskUtils.flattenTasks(TaskUtils.deepSortTasksByRank(tasks));
        this.loadTasks(this.tasks);
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  onFilterCleared(): void {
    this.openTrackStateSnackbar('filters');
    this.filterValueList.next([]);
    this.queryFilters = { ...this.queryFilters, fjson: `[]` };
    this.applyFilter(this.queryFilters);
    this.changeDetectorRef.detectChanges();
  }

  onFilterApplied(): void {
    this.openTrackStateSnackbar('filters');
    this.queryFilters = {
      ...this.queryFilters,
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
    };

    this.applyFilter(this.queryFilters);
    this._filtersOverlayBox.closeOverlay();
  }

  toggleCompleted(task: Task): void {
    task.is_complete = !task.is_complete;
    this.tasksService
      .updateTask(this.selectedTaskList.id, null, task)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdRef.markForCheck());
  }

  trackByFn(index: number, item: Task): number {
    return item.id || index;
  }

  goToTasks(task?: Task): void {
    let params: (string | number)[];
    if (task?.id) {
      params = ['../new-tasks', 'Project', task.project_id, task.task_list_id, 'list'];
    } else if (this.selectedTaskList.id) {
      params = [
        '../new-tasks',
        this.selectedTaskList.related_to_type,
        this.selectedTaskList.related_to_id,
        this.selectedTaskList.id,
        'list',
      ];
    } else {
      params = ['../new-tasks', 'Project', task.project_id];
    }

    this.router.navigate(params, { relativeTo: this.activatedRoute.parent.parent });
  }

  onTaskListChange(event: MatSelectChange): void {
    this.selectedTaskList = this.projectTaskLists.find((taskList) => taskList.id === event.value);
    this.panelDef = {
      ...this.panelDef,
      meta: {
        ...this.panelDef.meta,
        last_selected_task_list_id: event.value as number,
      },
    };
    this.silentPanelDefUpdated.emit(this.panelDef);
    this.getTasks(this.selectedTaskList.id);
  }

  onCloseByBackdropClick(): void {
    this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
  }

  onCellValueChanged(event: CellValueChangedEvent): void {
    if (event.colDef.field === 'is_complete') {
      const task = event.data as Task;
      this.tasksService
        .updateTask(this.selectedTaskList.id, null, task)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => this.cdRef.markForCheck());
    }
  }

  setColDefs(): void {
    this.columnDefs = [
      {
        label: '',
        name: 'is_complete',
        type: 'checkbox',
        tsTableOptions: {
          headerName: '',
          field: 'is_complete',
          width: 50,
          resizable: false,
        },
      },
      {
        label: 'Name',
        name: 'name',
        type: 'text',
        tsTableOptions: {
          headerName: 'Name',
          field: 'title',
          flex: 1,
        },
        meta: {
          readonly: true,
        },
      },
      {
        label: 'Due Date',
        name: 'due_date',
        type: 'date',
        tsTableOptions: {
          headerName: 'Due Date',
          field: 'due_date',
          flex: 1,
        },
        meta: {
          readonly: true,
        },
      },
      {
        label: 'Assigned To',
        name: 'assigned_user',
        type: 'text',
        tsTableOptions: {
          headerName: 'Assigned User',
          field: 'assigned_user_name',
          flex: 1,
        },
        meta: {
          readonly: true,
        },
      },
    ];
  }

  private checkTable(): void {
    this.newTable = this.account.features.includes(FeatureFlag.NEW_TABLE);
  }
}
